import { P } from '@stockpilot/shared';
import type { Property } from '@/api/endpoints';
import { Input, Select, Textarea } from '@/components/ui';
import { Can } from '@/components/Can';

export const AREA_UNITS = ['DECIMAL', 'ACRE', 'HECTARE', 'SQUARE_METRE', 'SQUARE_FOOT'];
export const PROPERTY_KINDS = ['LAND', 'PLOT', 'HOUSE', 'COMMERCIAL', 'OTHER'];

/** The statuses a person may set. SOLD is set by selling, never by hand. */
export const SETTABLE_STATUSES = ['AVAILABLE', 'RESERVED', 'WITHDRAWN'];

export interface PropertyFormState {
  reference: string;
  name: string;
  kind: string;
  status: string;
  titleNumber: string;
  plotNumber: string;
  blockNumber: string;
  location: string;
  district: string;
  coordinates: string;
  area: string;
  areaUnit: string;
  askingPrice: string;
  acquisitionCost: string;
  description: string;
  notes: string;
}

export const emptyPropertyForm: PropertyFormState = {
  reference: '', name: '', kind: 'LAND', status: 'AVAILABLE', titleNumber: '', plotNumber: '',
  blockNumber: '', location: '', district: '', coordinates: '', area: '', areaUnit: 'DECIMAL',
  askingPrice: '', acquisitionCost: '', description: '', notes: '',
};

/** A property, as the form holds it. Everything is a string while being typed. */
export function formFromProperty(property: Property): PropertyFormState {
  return {
    reference: property.reference,
    name: property.name,
    kind: property.kind,
    status: property.status,
    titleNumber: property.titleNumber ?? '',
    plotNumber: property.plotNumber ?? '',
    blockNumber: property.blockNumber ?? '',
    location: property.location ?? '',
    district: property.district ?? '',
    coordinates: property.coordinates ?? '',
    area: property.area ?? '',
    areaUnit: property.areaUnit,
    askingPrice: property.askingPrice,
    acquisitionCost: property.acquisitionCost ?? '',
    description: property.description ?? '',
    notes: property.notes ?? '',
  };
}

/**
 * The form as the API takes it.
 *
 * Money and size are numbers, not strings; an untouched optional field is left
 * out on create so the server keeps its own default, and sent as an empty
 * string on edit, where the server reads that as "there is none".
 */
export function propertyPayload(
  form: PropertyFormState,
  mode: 'create' | 'edit',
): Record<string, unknown> {
  const text = (value: string): string | undefined =>
    value === '' ? (mode === 'edit' ? '' : undefined) : value;

  return {
    reference: form.reference,
    name: form.name,
    kind: form.kind,
    ...(mode === 'edit' ? { status: form.status } : {}),
    titleNumber: text(form.titleNumber),
    plotNumber: text(form.plotNumber),
    blockNumber: text(form.blockNumber),
    location: text(form.location),
    district: text(form.district),
    coordinates: text(form.coordinates),
    ...(form.area === '' ? {} : { area: Number(form.area) }),
    areaUnit: form.areaUnit,
    askingPrice: Number(form.askingPrice),
    ...(form.acquisitionCost === '' ? {} : { acquisitionCost: Number(form.acquisitionCost) }),
    description: text(form.description),
    notes: text(form.notes),
  };
}

/** Enough to save: the rest of a title deed can follow later. */
export function propertyIsComplete(form: PropertyFormState): boolean {
  return Boolean(form.reference && form.name && form.askingPrice);
}

/**
 * Every field of a property, laid out once.
 *
 * Listing a plot and correcting one later ask for exactly the same details, so
 * they share a form — two copies would drift the moment a field was added to
 * one of them.
 */
export function PropertyFields({
  value,
  onChange,
  errors = {},
  showStatus = false,
}: {
  value: PropertyFormState;
  onChange: (next: PropertyFormState) => void;
  errors?: Record<string, string>;
  /** Only on edit: a new property is always available. */
  showStatus?: boolean;
}) {
  const set = (changes: Partial<PropertyFormState>): void => onChange({ ...value, ...changes });

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Input
          label="Reference"
          required
          placeholder="KIRA-014"
          value={value.reference}
          error={errors.reference}
          onChange={(event) => set({ reference: event.target.value })}
        />
        <Input
          label="Name"
          required
          placeholder="Kira Plot 14"
          value={value.name}
          error={errors.name}
          onChange={(event) => set({ name: event.target.value })}
        />
        <Select label="Kind" value={value.kind} onChange={(event) => set({ kind: event.target.value })}>
          {PROPERTY_KINDS.map((kind) => (
            <option key={kind} value={kind}>{kind.toLowerCase()}</option>
          ))}
        </Select>
      </div>

      {showStatus && (
        <Select
          label="Status"
          hint="A property becomes sold by recording a sale against it, never by hand."
          value={value.status}
          onChange={(event) => set({ status: event.target.value })}
        >
          {/* A sold property keeps showing its own status until the sale that
              made it sold is cancelled. */}
          {(value.status === 'SOLD' ? ['SOLD', ...SETTABLE_STATUSES] : SETTABLE_STATUSES).map(
            (status) => (
              <option key={status} value={status} disabled={status === 'SOLD'}>
                {status.toLowerCase()}
              </option>
            ),
          )}
        </Select>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <Input
          label="Title number"
          placeholder="FRV 1234 FOL 56"
          value={value.titleNumber}
          onChange={(event) => set({ titleNumber: event.target.value })}
        />
        <Input
          label="Plot number"
          value={value.plotNumber}
          onChange={(event) => set({ plotNumber: event.target.value })}
        />
        <Input
          label="Block number"
          value={value.blockNumber}
          onChange={(event) => set({ blockNumber: event.target.value })}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Input
          label="Location"
          placeholder="Kira Town Council"
          value={value.location}
          onChange={(event) => set({ location: event.target.value })}
        />
        <Input
          label="District"
          placeholder="Wakiso"
          value={value.district}
          onChange={(event) => set({ district: event.target.value })}
        />
        <Input
          label="Coordinates"
          placeholder="GPS or beacons"
          value={value.coordinates}
          onChange={(event) => set({ coordinates: event.target.value })}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Input
          label="Size"
          type="number"
          min="0"
          step="0.0001"
          value={value.area}
          onChange={(event) => set({ area: event.target.value })}
        />
        <Select
          label="Measured in"
          value={value.areaUnit}
          onChange={(event) => set({ areaUnit: event.target.value })}
        >
          {AREA_UNITS.map((unit) => (
            <option key={unit} value={unit}>{unit.toLowerCase().replace('_', ' ')}</option>
          ))}
        </Select>
        <Input
          label="Asking price"
          type="number"
          required
          min="0"
          step="0.01"
          value={value.askingPrice}
          error={errors.askingPrice}
          onChange={(event) => set({ askingPrice: event.target.value })}
        />
        <Can permission={P.PRODUCT_COST_VIEW}>
          <Input
            label="What it cost us"
            type="number"
            min="0"
            step="0.01"
            value={value.acquisitionCost}
            onChange={(event) => set({ acquisitionCost: event.target.value })}
          />
        </Can>
      </div>

      <Textarea
        label="Description"
        rows={2}
        value={value.description}
        onChange={(event) => set({ description: event.target.value })}
      />

      <Textarea
        label="Internal notes"
        rows={2}
        hint="Not printed on a receipt."
        value={value.notes}
        onChange={(event) => set({ notes: event.target.value })}
      />
    </div>
  );
}
